<script setup>
import { ref, computed } from 'vue'
import { useCabinetState } from '../composables/CabinetState'
import { useCatalogState } from '../composables/CatalogState'
import { useUserInventory } from '../composables/UserInventory'

const emit = defineEmits([ "escape" ]);

function escape() {
  emit("escape");
}

const {
  privateTrays,
  currentUserId,
  selectedTrayItem,
  isPrivateCloseChoice,
} = useCatalogState();

const {
  isReferencing,
  isOpening,
  isOpen,
  isPrevOpen,
  isClosing,
} = useCabinetState();

const {
  userPrivateTray,
  userPrivateTrayName,
  canOpenPrivateTray,
  autoSelectPrivateTray,
  availablePrivateTraysCount,
  openPrivateTray,
  closePrivateTray,
  showPrivateModal,
} = useUserInventory();
</script>

<template>
  <!-- Private trays to select -->
  <div class="mb-8 h-[800px]">

    <!-- Back button navigation -->
    <div class="mt-0 dark:text-gray-400">
      <Button type="primary" @click="() => escape()"><i class="fa fa-arrow-left"></i></Button>
      <span class="ml-4 text-lg font-normal">Nazaj na katalog</span>
    </div>

    <!-- <div class="mt-8 dark:text-gray-400">
      <span class="text-lg font-medium">Osebni predali</span>
      <span class="pl-2 relative" style="bottom: 1px" v-if="!autoSelectPrivateTray && privateTrays?.length">({{ privateTrays.length }})</span>
    </div> -->

    <!-- Display mode: list -->
    <div class="relative grid grid-cols-1 gap-4 w-full mt-2 mb-32" v-if="!autoSelectPrivateTray && privateTrays?.length > 0">
      <a href="#" v-for="(row, i) in privateTrays" @click.prevent="() => showPrivateModal(row)"
        class="py-4 flex bg-white border border-gray-200 rounded-lg shadow hover:border-blue-600 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">

        <!-- Status icon -->
        <div class="grid place-items-center text-gray-200 dark:text-gray-900 text-center w-24 mx-4"
          :class="{
            'text-gray-300': row.status == 'locked',
            'text-red-500': row.status == 'occupied' && row.userId != currentUserId,
            'text-blue-500': row.status == 'occupied' && row.userId == currentUserId,
            'text-green-500': row.status == 'active'
          }" style="font-size: 32px; width: 50px; height: 50px;">
          <i class="fa fa-user" :class="{ 'text-blue-500': row.status == 'occupied' && row.userId == currentUserId }" v-if="row.status == 'occupied' || row.userId == currentUserId"></i>
          <i class="fa fa-lock" v-else-if="row.status == 'locked'"></i>
          <i class="fa fa-inbox" v-else-if="row.status == 'active'"></i>
        </div>

        <!-- Tray name -->
        <div class="w-full pr-5 flex flex-row justify-between items-center gap-2">
          <div>
            <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.name">{{ row.name }}</h5>
            <h3 class="mt-0 text-blue-500" v-if="row.status == 'occupied' && row.userId == currentUserId">Moj predal</h3>
            <h3 class="mt-0 text-red-500" v-else-if="row.status == 'occupied'">Zaseden</h3>
            <h3 class="mt-0 text-gray-400" v-else-if="row.status == 'locked'">Zaklenjen</h3>
            <h3 class="mt-0 text-green-500" v-else-if="row.status == 'active'">Prost predal</h3>
          </div>
        </div>

      </a>
    </div>

    <!-- Display mode: auto select -->
    <div class="relative grid grid-cols-1 gap-4 w-full h-full mt-2 mb-32" v-if="autoSelectPrivateTray && privateTrays?.length > 0">

      <div class="grid place-items-center w-full h-full">
        <div class="text-center">
          <div class="grid place-items-center my-5" style="height: 64px;">
            <i class="fa fa-user text-blue-600" style="font-size: 64px;" v-if="!isReferencing && isOpen && !isPrevOpen && selectedTrayItem?.status == 'occupied'"></i>
            <i class="fa fa-user text-green-600" style="font-size: 64px;" v-else-if="!isReferencing && isOpen && !isPrevOpen && selectedTrayItem?.status == 'active'"></i>
            <Spinner size="12" v-else-if="isReferencing || isOpening || isClosing" />
            <!-- <i class="fa fa-inbox text-blue-600" style="font-size: 32px;"></i> -->
            <i class="fa fa-user-shield text-green-600 opacity-30" style="font-size: 64px;" v-if="!isReferencing && !isOpen && !isOpening && !isClosing && userPrivateTray?.status == 'occupied'"></i>
          </div>
          <div class="text-2xl">
            <span v-if="isReferencing">Postavljam omaro v referenco...</span>
            <span v-else-if="!isReferencing && !isOpen && isOpening">Odpiram predal <span class="font-semibold">{{ userPrivateTrayName }}</span>...</span>
            <span v-else-if="!isReferencing && isOpen && !isPrevOpen && selectedTrayItem?.status == 'occupied'">
              <span>Odprt predal <span class="font-semibold">{{ userPrivateTrayName }}</span> je namenjen vam.</span><br />
              <small class="text-sm">Zapri predal za nadaljevanje</small>
            </span>
            <span v-else-if="!isReferencing && isOpen && !isPrevOpen && selectedTrayItem?.status == 'active'">
              <span>Odprt predal <span class="font-semibold">{{ userPrivateTrayName }}</span> je prost.</span><br />
              <small class="text-sm">Zapri predal za nadaljevanje</small>
            </span>
            <span v-else-if="!isReferencing && isPrevOpen && !isOpening"><i class="fa fa-triangle-exclamation text-amber-500"></i>Za nadaljevanje najprej zapri prejšnji predal.</span>
            <span v-else-if="!isReferencing && !isOpen && !isOpening && userPrivateTray?.status == 'occupied'">V predalu <span class="font-semibold">{{ userPrivateTrayName }}</span> je vaša vsebina.</span>
            <span v-else-if="!isReferencing && !isOpen && !isOpening && availablePrivateTraysCount == 0">Noben predal ni na voljo.</span>
            <!-- <span v-else-if="!isReferencing && !isOpen && !isOpening && availablePrivateTraysCount > 0">Na voljo {{ availablePrivateTraysCount }} prosti predal.</span> -->
            <span v-else-if="!isReferencing && !isOpen && !isOpening && availablePrivateTraysCount > 0">Na voljo so prosti predali.</span>
          </div>
          <div class="text-xl my-5" style="min-height: 40px;">
            <!-- Open button -->
            <Button size="xl" :type="!isReferencing && !isOpen && !isOpening && userPrivateTray?.status == 'occupied' ? 'success' : 'primary'" @click="openPrivateTray()" v-if="!isPrivateCloseChoice && canOpenPrivateTray">Odpri</Button>
            
            <!-- Message to close tray -->
            <span v-if="!isReferencing && isOpen && !isOpening && !isPrivateCloseChoice">Za nadaljevanje zapri predal.</span>
            
            <!-- Choice to select private tray status -->
            <div class="mt-16" v-if="!isReferencing && !isOpening && isPrivateCloseChoice">
              <span class="text-xl">{{ $t('catalog.tray_state_select') }}</span>
              <div class="grid grid-cols-2 mt-4">
                <div class="text-right pr-8 pl-20">
                  <Button size="xl" type="warning" @click="closePrivateTray('occupied')">Zaseden</Button>
                  <p class="text-md mt-4 text-gray-500">{{ $t('catalog.keep_tray_option') }}</p>
                </div>
                <div class="text-left pl-8 pr-16 border-l border-gray-300">
                  <Button size="xl" type="success" @click="closePrivateTray('active')">Prost</Button>
                  <p class="text-md mt-4 text-gray-500">{{ $t('catalog.empty_tray_option') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- No article found -->
    <div class="relative sm:grid sm:grid-cols-1 gap-4 w-full mt-2 mb-32" v-if="!privateTrays || privateTrays.length == 0">
      <span class="text-slate-500">Ni osebnih predalov na voljo.</span>
    </div>

  </div>
</template>